import { useEffect, useState } from 'react';
import { Dialog } from '~/components/admin/Dialog';

/** Eén plek voor sneltoetsen, met twee regels die de losse handlers misten:
 *
 *  1. Een sneltoets vuurt NIET terwijl er een dialoog open staat. Ctrl+S
 *     bewaarde anders het artikel terwijl je in het zoekveld van de
 *     artikelkiezer typte, met de dialoog nog open.
 *  2. Toetsen zonder modifier vuren niet in een invoerveld — anders typ je
 *     geen "t" meer in een titel.
 *
 *  Bewust NIET gebruikt: Ctrl+T (nieuw tabblad) en Ctrl+0/+/− (browserzoom).
 *  Die kun je niet betrouwbaar onderscheppen; de oude app probeerde dat wel. */
export interface Shortcut {
  keys: string;                 // weergave, bijv. "Ctrl + S"
  description: string;
  match: (e: KeyboardEvent) => boolean;
  run: () => void;
  /** Mag ook vuren terwijl een dialoog open staat (bijv. Escape). */
  inDialog?: boolean;
}

const isTypingTarget = (el: EventTarget | null) =>
  el instanceof HTMLElement &&
  (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);

export function useShortcuts(shortcuts: Shortcut[], enabled = true) {
  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      const dialogOpen = document.querySelector('[role="dialog"]') !== null;
      const hasModifier = e.ctrlKey || e.metaKey || e.altKey;
      for (const s of shortcuts) {
        if (dialogOpen && !s.inDialog) continue;
        if (!hasModifier && isTypingTarget(e.target)) continue;
        if (!s.match(e)) continue;
        e.preventDefault();
        s.run();
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shortcuts, enabled]);
}

/** `?` opent het overzicht. Zonder dit blijven sneltoetsen kennis die je
 *  alleen hebt als iemand het je vertelt. */
export function ShortcutHelp({ shortcuts }: { shortcuts: Shortcut[] }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== '?' || isTypingTarget(e.target)) return;
      if (document.querySelector('[role="dialog"]')) return;
      e.preventDefault();
      setOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  if (!open) return null;

  return (
    <Dialog title="Sneltoetsen" onClose={() => setOpen(false)} width="28rem">
      <ul className="divide-y divide-hairline">
        {[...shortcuts, { keys: '?', description: 'Dit overzicht', match: () => false, run: () => {} }].map((s) => (
          <li key={s.keys} className="flex items-center justify-between gap-4 py-2">
            <span className="text-base text-ink-2">{s.description}</span>
            <span className="flex shrink-0 gap-1">
              {s.keys.split('+').map((k) => (
                <kbd key={k} className="rounded-sm border border-hairline-strong bg-sunken px-1.5 py-0.5 font-mono text-xs text-ink">
                  {k.trim()}
                </kbd>
              ))}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-sm text-ink-3">
        Sneltoetsen werken niet terwijl een venster open staat of terwijl je in een tekstveld typt.
      </p>
    </Dialog>
  );
}
